import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  InputLabel,
  List,
  ListItemButton,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import HistoryIcon from '@mui/icons-material/History';
import CodeMirror from '@uiw/react-codemirror';
import { markdown } from '@codemirror/lang-markdown';
import { yaml } from '@codemirror/lang-yaml';
import { PageHeader } from '../components/PageHeader';
import { OpsApiError } from '../api/client';
import {
  useBrainDocs,
  useBrainRevisions,
  useMe,
  useRestoreBrainDoc,
  useRetireBrainDoc,
  useUpsertBrainDoc,
} from '../hooks/useOps';
import type { BrainDoc, BrainFormat, BrainKind, BrainRevision, UpsertBrainDoc } from '../api/types';

interface KindMeta {
  kind: BrainKind;
  label: string;
  singleton: boolean;
  /** kinds whose metadata is load-bearing (machine consumer) — show the editor. */
  structured: boolean;
}

const KINDS: KindMeta[] = [
  { kind: 'soul', label: 'Soul', singleton: true, structured: false },
  { kind: 'company_identity', label: 'Company identity', singleton: true, structured: false },
  { kind: 'agent_identity', label: 'Agent identity', singleton: true, structured: false },
  { kind: 'user_profile', label: 'User profile', singleton: true, structured: false },
  { kind: 'bot_persona', label: 'Bot persona', singleton: true, structured: false },
  { kind: 'tools_notes', label: 'Tools notes', singleton: true, structured: false },
  { kind: 'rule', label: 'Rules', singleton: false, structured: false },
  { kind: 'command', label: 'Commands', singleton: false, structured: true },
  { kind: 'subagent', label: 'Sub-agents', singleton: false, structured: true },
  { kind: 'skill_def', label: 'Skill definitions', singleton: false, structured: true },
];

const FORMATS: BrainFormat[] = ['markdown', 'yaml', 'json'];
const labelFor = (k: string) => KINDS.find((m) => m.kind === k)?.label ?? k;
const metaFor = (k: string) => KINDS.find((m) => m.kind === k);

/** A doc currently open in the editor (existing on the server, or a fresh draft). */
interface EditorState {
  isNew: boolean;
  kind: BrainKind;
  slug: string;
  title: string;
  format: BrainFormat;
  content: string;
  metadataText: string;
  /** The version we loaded — sent as expectedVersion for optimistic concurrency. */
  baseVersion: number;
  allowSecret: boolean;
}

function cmExtensions(format: BrainFormat) {
  if (format === 'yaml') return [yaml()];
  if (format === 'json') return [];
  return [markdown()];
}

/** Minimal line diff: marks each line as added/removed/equal (LCS-free, good enough). */
function lineDiff(oldText: string, newText: string): Array<{ type: 'add' | 'del' | 'eq'; text: string }> {
  const a = oldText.split('\n');
  const b = newText.split('\n');
  const out: Array<{ type: 'add' | 'del' | 'eq'; text: string }> = [];
  const bset = new Set(b);
  const aset = new Set(a);
  // Simple per-line presence diff — readable for prose/config without a dep.
  const max = Math.max(a.length, b.length);
  for (let i = 0; i < max; i++) {
    const al = a[i];
    const bl = b[i];
    if (al === bl) {
      out.push({ type: 'eq', text: al ?? '' });
    } else {
      if (al !== undefined && !bset.has(al)) out.push({ type: 'del', text: al });
      if (bl !== undefined && !aset.has(bl)) out.push({ type: 'add', text: bl });
      if (al !== undefined && bset.has(al) && bl !== undefined && aset.has(bl) && al !== bl) {
        // both lines exist elsewhere — show as a swap
        out.push({ type: 'del', text: al });
        out.push({ type: 'add', text: bl });
      }
    }
  }
  return out;
}

export function AgentBrainPage() {
  const { data: me } = useMe();
  const canEdit = Boolean(me?.capability.canManageBrain);

  const { data: docs, isLoading, error } = useBrainDocs();
  const upsert = useUpsertBrainDoc();
  const retire = useRetireBrainDoc();
  const restore = useRestoreBrainDoc();

  const [editor, setEditor] = useState<EditorState | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [secretPrompt, setSecretPrompt] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [revisionsOpen, setRevisionsOpen] = useState(false);

  // Block a browser tab close/refresh while there are unsaved edits.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const grouped = useMemo(() => {
    const byKind = new Map<string, BrainDoc[]>();
    for (const d of docs ?? []) {
      const arr = byKind.get(d.kind) ?? [];
      arr.push(d);
      byKind.set(d.kind, arr);
    }
    return byKind;
  }, [docs]);

  function confirmDiscard(): boolean {
    if (!dirty) return true;
    return window.confirm('You have unsaved changes. Discard them?');
  }

  function openDoc(doc: BrainDoc) {
    if (!confirmDiscard()) return;
    setSaveError(null);
    setEditor({
      isNew: false,
      kind: doc.kind,
      slug: doc.slug,
      title: doc.title,
      format: doc.format,
      content: doc.content,
      metadataText: JSON.stringify(doc.metadata ?? {}, null, 2),
      baseVersion: doc.version,
      allowSecret: Boolean(doc.allowSecret),
    });
    setDirty(false);
  }

  function startNew(kind: BrainKind, slug: string, title: string, format: BrainFormat) {
    setSaveError(null);
    setEditor({
      isNew: true,
      kind,
      slug,
      title,
      format,
      content: '',
      metadataText: '{}',
      baseVersion: 0,
      allowSecret: false,
    });
    setDirty(true);
    setNewOpen(false);
  }

  function patch(p: Partial<EditorState>) {
    setEditor((cur) => (cur ? { ...cur, ...p } : cur));
    setDirty(true);
    setSaveError(null);
  }

  async function doSave(allowSecret: boolean) {
    if (!editor) return;
    let metadata: Record<string, unknown> = {};
    if (editor.metadataText.trim()) {
      try {
        metadata = JSON.parse(editor.metadataText);
      } catch {
        setSaveError('metadata is not valid JSON');
        return;
      }
    }
    const body: UpsertBrainDoc = {
      kind: editor.kind,
      slug: editor.slug,
      title: editor.title,
      format: editor.format,
      content: editor.content,
      metadata,
      allowSecret: allowSecret || undefined,
      // Optimistic concurrency: 0 for a new doc, the loaded version for an edit.
      expectedVersion: editor.baseVersion,
    };
    try {
      const saved = await upsert.mutateAsync(body);
      setDirty(false);
      setSecretPrompt(null);
      setSaveError(null);
      // Reload the just-saved version so the next edit's expectedVersion is correct.
      setEditor((cur) =>
        cur ? { ...cur, isNew: false, baseVersion: saved.version, allowSecret: Boolean(saved.allowSecret) } : cur,
      );
    } catch (e) {
      if (e instanceof OpsApiError && e.errorCode === 'VALIDATION_FAILED' && /secret/i.test(e.message)) {
        setSecretPrompt(e.message); // offer the deliberate allowSecret override
        return;
      }
      setSaveError(e instanceof Error ? e.message : 'save failed');
    }
  }

  async function doRetire() {
    if (!editor || editor.isNew) return;
    if (!window.confirm(`Retire ${editor.kind}/${editor.slug}? It leaves the bundle (restorable).`)) return;
    await retire.mutateAsync({ kind: editor.kind, slug: editor.slug });
    setEditor(null);
    setDirty(false);
  }

  const structured = editor ? metaFor(editor.kind)?.structured : false;

  return (
    <Box>
      <PageHeader
        title="Agent brain"
        action={
          canEdit && (
            <Button variant="contained" onClick={() => setNewOpen(true)}>
              New document
            </Button>
          )
        }
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        The canonical agent brain — identity, rules, commands, and skill definitions — served to
        every surface over MCP. Edits here change what all agents read; saves are secret-scanned and
        versioned. <strong>{labelFor('soul')}</strong> is re-read by Hermes on every message.
      </Alert>

      {!canEdit && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You can view the brain but only a <strong>founder</strong> can save changes.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error).message}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} gap={2} alignItems="flex-start">
        {/* ── Doc list ── */}
        <Paper variant="outlined" sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0 }}>
          {isLoading && <Typography sx={{ p: 2 }} color="text.secondary">Loading…</Typography>}
          {!isLoading && (docs?.length ?? 0) === 0 && (
            <Typography sx={{ p: 2 }} color="text.secondary">
              No documents yet. Click <strong>New document</strong> or run the seed importer.
            </Typography>
          )}
          <List dense disablePadding>
            {KINDS.map((km) => {
              const items = grouped.get(km.kind) ?? [];
              if (items.length === 0) return null;
              return (
                <li key={km.kind}>
                  <ul style={{ padding: 0 }}>
                    <ListSubheader>{km.label}</ListSubheader>
                    {items.map((d) => (
                      <ListItemButton
                        key={`${d.kind}/${d.slug}`}
                        selected={editor?.kind === d.kind && editor?.slug === d.slug && !editor.isNew}
                        onClick={() => openDoc(d)}
                      >
                        <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                          <Typography variant="body2" noWrap>
                            {d.title}
                          </Typography>
                          <Chip size="small" variant="outlined" label={`v${d.version}`} sx={{ height: 18 }} />
                          {d.allowSecret && (
                            <Tooltip title="Stored as a secret — excluded from the bundle">
                              <LockIcon fontSize="inherit" color="warning" />
                            </Tooltip>
                          )}
                        </Stack>
                      </ListItemButton>
                    ))}
                  </ul>
                </li>
              );
            })}
          </List>
        </Paper>

        {/* ── Editor ── */}
        <Paper variant="outlined" sx={{ flexGrow: 1, p: 2, minWidth: 0, width: '100%' }}>
          {!editor ? (
            <Typography color="text.secondary">Select a document, or create a new one.</Typography>
          ) : (
            <Stack gap={2}>
              <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
                <Chip label={labelFor(editor.kind)} color="primary" variant="outlined" />
                <Typography variant="body2" fontFamily="monospace" color="text.secondary">
                  {editor.kind}/{editor.slug}
                </Typography>
                {!editor.isNew && (
                  <Chip size="small" variant="outlined" label={`v${editor.baseVersion}`} />
                )}
                {editor.allowSecret && <Chip size="small" color="warning" icon={<LockIcon />} label="secret" />}
                <Box flexGrow={1} />
                {!editor.isNew && (
                  <Button
                    size="small"
                    startIcon={<HistoryIcon />}
                    onClick={() => setRevisionsOpen(true)}
                  >
                    History
                  </Button>
                )}
              </Stack>

              <Stack direction="row" gap={2} flexWrap="wrap">
                <TextField
                  label="Title"
                  size="small"
                  value={editor.title}
                  onChange={(e) => patch({ title: e.target.value })}
                  disabled={!canEdit}
                  sx={{ flexGrow: 1, minWidth: 220 }}
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Format</InputLabel>
                  <Select
                    label="Format"
                    value={editor.format}
                    onChange={(e) => patch({ format: e.target.value as BrainFormat })}
                    disabled={!canEdit}
                  >
                    {FORMATS.map((f) => (
                      <MenuItem key={f} value={f}>
                        {f}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                <CodeMirror
                  value={editor.content}
                  height="380px"
                  editable={canEdit}
                  extensions={cmExtensions(editor.format)}
                  onChange={(v) => patch({ content: v })}
                />
              </Box>

              {structured && (
                <TextField
                  label="Metadata (JSON)"
                  helperText={
                    editor.kind === 'skill_def'
                      ? 'Required: { "name": "...", "description": "..." }'
                      : editor.kind === 'command'
                        ? 'Optional: { "args": [ ... ] }'
                        : 'Optional: { "tools": [ ... ] }'
                  }
                  multiline
                  minRows={3}
                  value={editor.metadataText}
                  onChange={(e) => patch({ metadataText: e.target.value })}
                  disabled={!canEdit}
                  sx={{ fontFamily: 'monospace' }}
                />
              )}

              {saveError && <Alert severity="error">{saveError}</Alert>}

              <Stack direction="row" gap={1}>
                <Button
                  variant="contained"
                  onClick={() => doSave(false)}
                  disabled={!canEdit || !dirty || upsert.isPending}
                >
                  {upsert.isPending ? 'Saving…' : 'Save'}
                </Button>
                {!editor.isNew && (
                  <Button color="error" onClick={doRetire} disabled={!canEdit}>
                    Retire
                  </Button>
                )}
                {dirty && (
                  <Typography variant="caption" color="warning.main" sx={{ alignSelf: 'center' }}>
                    unsaved changes
                  </Typography>
                )}
              </Stack>
            </Stack>
          )}
        </Paper>
      </Stack>

      <NewDocDialog open={newOpen} onClose={() => setNewOpen(false)} onCreate={startNew} existing={docs ?? []} />

      {/* Secret-scan reject → deliberate per-save override (NOT a sticky checkbox). */}
      <Dialog open={Boolean(secretPrompt)} onClose={() => setSecretPrompt(null)}>
        <DialogTitle>This document looks like it contains a secret</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body2" sx={{ mb: 1 }}>
              {secretPrompt}
            </Typography>
            <Typography variant="body2">
              If this credential is intentional, you can store it as a secret. It will be
              <strong> excluded from the agent bundle</strong> so it never leaks to a surface.
              Otherwise, cancel and remove the credential.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSecretPrompt(null)}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={() => doSave(true)}>
            Store as secret
          </Button>
        </DialogActions>
      </Dialog>

      <RevisionsDrawer
        open={revisionsOpen}
        onClose={() => setRevisionsOpen(false)}
        kind={editor && !editor.isNew ? editor.kind : null}
        slug={editor && !editor.isNew ? editor.slug : null}
        current={editor?.content ?? ''}
        canEdit={canEdit}
        onRestore={async (kind, slug) => {
          await restore.mutateAsync({ kind, slug });
          setRevisionsOpen(false);
        }}
      />
    </Box>
  );
}

// ── New document dialog ───────────────────────────────────────────────────────

function NewDocDialog({
  open,
  onClose,
  onCreate,
  existing,
}: {
  open: boolean;
  onClose: () => void;
  onCreate: (kind: BrainKind, slug: string, title: string, format: BrainFormat) => void;
  existing: BrainDoc[];
}) {
  const [kind, setKind] = useState<BrainKind>('rule');
  const [slug, setSlug] = useState('');
  const [title, setTitle] = useState('');
  const [format, setFormat] = useState<BrainFormat>('markdown');
  const meta = metaFor(kind);
  const effectiveSlug = meta?.singleton ? 'main' : slug.trim();
  const slugValid = meta?.singleton || /^[a-z0-9-]+$/.test(effectiveSlug);
  const collides = existing.some((d) => d.kind === kind && d.slug === effectiveSlug);

  function reset() {
    setKind('rule');
    setSlug('');
    setTitle('');
    setFormat('markdown');
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New brain document</DialogTitle>
      <DialogContent>
        <Stack gap={2} sx={{ mt: 1 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Kind</InputLabel>
            <Select label="Kind" value={kind} onChange={(e) => setKind(e.target.value as BrainKind)}>
              {KINDS.map((m) => (
                <MenuItem key={m.kind} value={m.kind}>
                  {m.label} {m.singleton ? '(single)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {!meta?.singleton && (
            <TextField
              label="Slug"
              size="small"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              error={slug.length > 0 && !slugValid}
              helperText="lowercase, digits, hyphens — becomes a file path on the consumer"
            />
          )}
          <TextField
            label="Title"
            size="small"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Format</InputLabel>
            <Select label="Format" value={format} onChange={(e) => setFormat(e.target.value as BrainFormat)}>
              {FORMATS.map((f) => (
                <MenuItem key={f} value={f}>
                  {f}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {collides && (
            <Alert severity="warning">
              A {labelFor(kind)} with slug “{effectiveSlug}” already exists — open it instead.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!title.trim() || !slugValid || collides}
          onClick={() => {
            onCreate(kind, effectiveSlug, title.trim(), format);
            reset();
          }}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Revision history + diff + restore ─────────────────────────────────────────

function RevisionsDrawer({
  open,
  onClose,
  kind,
  slug,
  current,
  canEdit,
  onRestore,
}: {
  open: boolean;
  onClose: () => void;
  kind: string | null;
  slug: string | null;
  current: string;
  canEdit: boolean;
  onRestore: (kind: string, slug: string) => void;
}) {
  const { data: revisions, isLoading } = useBrainRevisions(open ? kind : null, open ? slug : null);
  const [compare, setCompare] = useState<BrainRevision | null>(null);

  useEffect(() => {
    if (!open) setCompare(null);
  }, [open]);

  const diff = compare ? lineDiff(compare.content, current) : null;
  const isRetired = (revisions ?? []).every((r) => !r.current);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: '100vw', sm: 520 }, p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Revisions {kind ? `· ${kind}/${slug}` : ''}
        </Typography>
        {isLoading && <Typography color="text.secondary">Loading…</Typography>}
        {isRetired && (revisions?.length ?? 0) > 0 && canEdit && kind && slug && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <Button size="small" onClick={() => onRestore(kind, slug)}>
                Restore
              </Button>
            }
          >
            This document is retired. Restore copies its latest revision forward as a new version.
          </Alert>
        )}
        <List dense>
          {(revisions ?? []).map((r) => (
            <ListItemButton
              key={r.version}
              selected={compare?.version === r.version}
              onClick={() => setCompare(r)}
            >
              <Stack direction="row" gap={1} alignItems="center">
                <Chip size="small" label={`v${r.version}`} variant="outlined" />
                {r.current && <Chip size="small" color="success" label="current" />}
                <Typography variant="body2" noWrap>
                  {r.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(r.updatedAt).toLocaleString()}
                </Typography>
              </Stack>
            </ListItemButton>
          ))}
        </List>

        {diff && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" color="text.secondary">
              Diff: v{compare?.version} → editor content
            </Typography>
            <Box
              sx={{
                mt: 1,
                fontFamily: 'monospace',
                fontSize: 12,
                whiteSpace: 'pre-wrap',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1,
                maxHeight: 360,
                overflow: 'auto',
              }}
            >
              {diff.map((l, i) => (
                <Box
                  key={i}
                  sx={{
                    bgcolor:
                      l.type === 'add'
                        ? 'success.light'
                        : l.type === 'del'
                          ? 'error.light'
                          : 'transparent',
                    color: l.type === 'eq' ? 'text.secondary' : 'text.primary',
                    opacity: l.type === 'eq' ? 0.7 : 1,
                  }}
                >
                  {l.type === 'add' ? '+ ' : l.type === 'del' ? '- ' : '  '}
                  {l.text}
                </Box>
              ))}
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
}
